import { useCallback } from 'react';

import { UseQueryResult, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { uniqBy } from 'lodash';
import requestGetHeroPlannerCitySegments from '~/queries/customerCommunication/requestGetHeroPlannerCitySegments';
import requestGetHeroPlannerCountries from '~/queries/customerCommunication/requestGetHeroPlannerCountries';
import requestGetHeroPlannerCountryGroups from '~/queries/customerCommunication/requestGetHeroPlannerCountryGroups';
import requestGetHeroPlannerCountryStates from '~/queries/customerCommunication/requestGetHeroPlannerCountryStates';
import requestGetHeroPlannerMemberships from '~/queries/customerCommunication/requestGetHeroPlannerMemberships';
import requestGetHeroPlannerSchedulesList from '~/queries/customerCommunication/requestGetHeroPlannerSchedulesList';
import requestGetHeroPlannerStateCities from '~/queries/customerCommunication/requestGetHeroPlannerStateCities';
import requestGetHeroPlannerStateSchedulesList from '~/queries/customerCommunication/requestGetHeroPlannerStateSchedulesList';
import requestGetTodaysEscapesScheduleDebug, {
  HeroPlannerScheduleDebugOutcome,
} from '~/queries/customerCommunication/requestGetTodaysEscapesScheduleDebug';
import requestGetTodaysEscapesScheduleGenerationStatus, {
  TodaysEscapesScheduleGenerationStatus,
} from '~/queries/customerCommunication/requestGetTodaysEscapesScheduleGenerationStatus';
import { TodaysEscapesScheduleGenerationSegment } from '~/queries/customerCommunication/requestPostTodaysEscapesScheduleGeneration';

import {
  HERO_PLANNER_DEBUG_DEFAULT_LOYALTY_ID,
  HeroPlannerDebugHeroRows,
  HeroPlannerDebugTarget,
  getHeroPlannerDebugRowLocations,
  getHeroPlannerDebugSkipReason,
  getHeroPlannerDebugTargetKey,
} from '~/components/CustomerCommunication/HeroPlanner/utils/heroPlannerDebugUtils';
import { getHeroPlannerGenerationSegments } from '~/components/CustomerCommunication/HeroPlanner/utils/heroPlannerGenerationUtils';

import { createConcurrencyLimiter } from '~/utils/createConcurrencyLimiter';

// A full sweep is several hundred requests; cap how many hit svc-customer-communication at once. Shared across every
// query in the sweep (and the discovery walk) so the cap holds regardless of how react-query schedules them.
const MAX_CONCURRENT_DEBUG_REQUESTS = 8;
const runLimited = createConcurrencyLimiter(MAX_CONCURRENT_DEBUG_REQUESTS);

// the debugger checks each segment's default cadence only; loyalty is checked per variant
const DEBUG_CADENCE_ID = 'DEFAULT';
const DEFAULT_LOYALTY = { id: HERO_PLANNER_DEBUG_DEFAULT_LOYALTY_ID, name: 'Default' };

// svc-customer-communication caps the hero planner list endpoint at this many rows (`take: 100` in getHeroDeals)
const HERO_PLANNER_LIST_MAX_ROWS = 100;

export interface HeroPlannerDebugHeroRowsResult {
  /** Only holds the country group when its list was complete, since a partial list could match the wrong row */
  heroRows: HeroPlannerDebugHeroRows;
  /** Whether the country group's hero planner rows couldn't be fully loaded */
  isIncomplete: boolean;
}

export interface HeroPlannerDebugCountryGroupHeroRows {
  /** Every selected country group's hero planner rows that could be fully loaded */
  heroRows: HeroPlannerDebugHeroRows;
  /** Country groups whose hero planner rows are still loading */
  pendingCountryGroupIds: Set<string>;
  /** Country groups whose hero planner rows couldn't be fully loaded */
  incompleteCountryGroupIds: Array<string>;
}

export interface HeroPlannerDebugPreSendCheck {
  countryGroup: string;
  /** Only set for lifestyle brands, which are checked per segment */
  segment?: TodaysEscapesScheduleGenerationSegment;
  query: UseQueryResult<TodaysEscapesScheduleGenerationStatus>;
}

export interface HeroPlannerDebugDiscoveryError {
  /** Human readable location of the lookup that failed, e.g. "Europe › South Africa" */
  path: string;
  message: string;
}

export interface HeroPlannerDebugTargets {
  targets: Array<HeroPlannerDebugTarget>;
  /** Lookups that failed part way down the tree; the rest of the tree is still returned */
  discoveryErrors: Array<HeroPlannerDebugDiscoveryError>;
}

export interface HeroPlannerDebugCountryGroupTargets {
  countryGroup: CustomerCommunication.HeroPlannerCountryGroup;
  query: UseQueryResult<HeroPlannerDebugTargets>;
}

export interface HeroPlannerDebugSelectedTargets {
  /** Each selected country group's discovery, in the order the country groups were passed in */
  countryGroups: Array<HeroPlannerDebugCountryGroupTargets>;
  /** Targets across every selected country group that has been discovered */
  targets: Array<HeroPlannerDebugTarget>;
  discoveryErrors: Array<HeroPlannerDebugDiscoveryError>;
  /** Whether any selected country group is still being discovered */
  isPending: boolean;
}

export const heroPlannerScheduleDebugQueryKeys = {
  all: ['heroPlannerScheduleDebug'] as const,
  countryGroups: (brand: string) => [...heroPlannerScheduleDebugQueryKeys.all, 'countryGroups', brand] as const,
  targets: (brand: string, countryGroupId: string) =>
    [...heroPlannerScheduleDebugQueryKeys.all, 'targets', brand, countryGroupId] as const,
  heroRowsAll: () => [...heroPlannerScheduleDebugQueryKeys.all, 'heroRows'] as const,
  heroRows: (brand: string, sendDate: string, countryGroupId: string) =>
    [...heroPlannerScheduleDebugQueryKeys.heroRowsAll(), brand, sendDate, countryGroupId] as const,
  schedules: () => [...heroPlannerScheduleDebugQueryKeys.all, 'schedule'] as const,
  schedule: (brand: string, sendDate: string, targetKey: string) =>
    [...heroPlannerScheduleDebugQueryKeys.schedules(), brand, sendDate, targetKey] as const,
  preSendChecks: () => [...heroPlannerScheduleDebugQueryKeys.all, 'preSendCheck'] as const,
  preSendCheck: (brand: string, sendDate: string, countryGroup: string, segment?: string) =>
    [...heroPlannerScheduleDebugQueryKeys.preSendChecks(), brand, sendDate, countryGroup, segment ?? 'ALL'] as const,
};

function errorToMessage(error: unknown) {
  if (error instanceof Error) {
    return error.message;
  }
  // the hero planner request helpers throw pre-stringified errors
  return typeof error === 'string' ? error : JSON.stringify(error);
}

/**
 * Runs `fetchChildren` for every parent, collecting what succeeds and recording what fails instead of failing the
 * whole sweep - a single broken country shouldn't hide every other segment.
 */
async function expandLevel<TParent, TChild>(
  parents: Array<TParent>,
  fetchChildren: (parent: TParent) => Promise<Array<TChild>>,
  describeParent: (parent: TParent) => string,
  discoveryErrors: Array<HeroPlannerDebugDiscoveryError>,
): Promise<Array<{ parent: TParent; child: TChild }>> {
  const settled = await Promise.allSettled(parents.map((parent) => runLimited(() => fetchChildren(parent))));

  return settled.flatMap((result, index) => {
    const parent = parents[index];
    if (result.status === 'rejected') {
      discoveryErrors.push({ path: describeParent(parent), message: errorToMessage(result.reason) });
      return [];
    }
    return result.value.map((child) => ({ parent, child }));
  });
}

async function discoverHeroPlannerDebugTargets(
  brand: string,
  countryGroup: CustomerCommunication.HeroPlannerCountryGroup,
): Promise<HeroPlannerDebugTargets> {
  const discoveryErrors: Array<HeroPlannerDebugDiscoveryError> = [];

  // one country group at a time, so opening the debugger doesn't walk every group's tree at once
  const countries = await expandLevel(
    [countryGroup],
    (countryGroup) => requestGetHeroPlannerCountries(brand, countryGroup.id),
    (countryGroup) => countryGroup.name,
    discoveryErrors,
  );
  const states = await expandLevel(
    countries.map(({ parent, child }) => ({ countryGroup: parent, country: child })),
    ({ countryGroup, country }) => requestGetHeroPlannerCountryStates(brand, countryGroup.id, country.id),
    ({ countryGroup, country }) => `${countryGroup.name} › ${country.name}`,
    discoveryErrors,
  );
  const cities = await expandLevel(
    states.map(({ parent, child }) => ({ ...parent, state: child })),
    ({ countryGroup, country, state }) =>
      requestGetHeroPlannerStateCities(brand, countryGroup.id, country.id, state.id),
    ({ countryGroup, country, state }) => `${countryGroup.name} › ${country.name} › ${state.name}`,
    discoveryErrors,
  );
  const segments = await expandLevel(
    cities.map(({ parent, child }) => ({ ...parent, city: child })),
    ({ countryGroup, country, state, city }) =>
      requestGetHeroPlannerCitySegments(brand, countryGroup.id, country.id, state.id, city.id),
    ({ countryGroup, country, state, city }) => `${countryGroup.name} › ${country.name} › ${state.name} › ${city.name}`,
    discoveryErrors,
  );

  const segmentTargets = segments.map(({ parent, child: segment }) => ({ ...parent, segment }));

  // LuxPlus subscribers can get their own hero row and member-only tail deals, so every loyalty variant is checked.
  // Never-sent segments only ever get one (skipped) row, so their loyalties aren't looked up.
  const loyaltiesBySegment = await Promise.allSettled(
    segmentTargets.map(({ countryGroup, country, state, city, segment }) => {
      const identifier = {
        countryGroup: countryGroup.id,
        country: country.id,
        state: state.id,
        city: city.id,
        segment: segment.id,
        loyalty: DEFAULT_LOYALTY.id,
      };
      if (getHeroPlannerDebugSkipReason(identifier, brand, undefined)) {
        return Promise.resolve([DEFAULT_LOYALTY]);
      }
      return runLimited(() =>
        requestGetHeroPlannerMemberships(
          brand,
          countryGroup.id,
          country.id,
          state.id,
          city.id,
          segment.id,
          DEBUG_CADENCE_ID,
        ),
      );
    }),
  );

  const targets = segmentTargets.flatMap(({ countryGroup, country, state, city, segment }, index) => {
    const result = loyaltiesBySegment[index];
    let loyalties = result.status === 'fulfilled' ? result.value : [];
    if (result.status === 'rejected') {
      discoveryErrors.push({
        path: `${countryGroup.name} › ${country.name} › ${state.name} › ${city.name} › ${segment.name} (loyalties)`,
        message: errorToMessage(result.reason),
      });
    }
    // still check the default variant when the lookup failed or came back empty
    if (!loyalties.some((loyalty) => loyalty.id === DEFAULT_LOYALTY.id)) {
      loyalties = [DEFAULT_LOYALTY, ...loyalties];
    }

    return loyalties.map((loyalty) => {
      const identifier = {
        countryGroup: countryGroup.id,
        country: country.id,
        state: state.id,
        city: city.id,
        segment: segment.id,
        loyalty: loyalty.id,
      };
      return {
        ...identifier,
        key: getHeroPlannerDebugTargetKey(identifier),
        countryGroupName: countryGroup.name,
        countryName: country.name,
        stateName: state.name,
        cityName: city.name,
        segmentName: segment.name,
        loyaltyName: loyalty.name,
      };
    });
  });

  return { targets, discoveryErrors };
}

/** The brand's country groups; the debugger works through one at a time unless every group is asked for */
export function useGetHeroPlannerDebugCountryGroups(brand: string) {
  return useQuery({
    queryKey: heroPlannerScheduleDebugQueryKeys.countryGroups(brand),
    queryFn: () => requestGetHeroPlannerCountryGroups(brand),
    enabled: !!brand,
    // the geography tree changes with deploys, not during a debugging session
    staleTime: Infinity,
  });
}

/**
 * Walks each country group's countries › states › cities › segments › loyalties to find every identifier a hero
 * planner send goes out to (at the default cadence). Cached per country group for the visit, so switching back - or
 * between one group and all of them - is free.
 */
export function useGetHeroPlannerDebugTargets(
  brand: string,
  countryGroups: Array<CustomerCommunication.HeroPlannerCountryGroup>,
): HeroPlannerDebugSelectedTargets {
  // react-query only re-runs combine when a result or this function changes, so the targets keep their identity
  const combine = useCallback(
    (results: Array<UseQueryResult<HeroPlannerDebugTargets>>): HeroPlannerDebugSelectedTargets => {
      const discovered = results.flatMap((result) => (result.data ? [result.data] : []));
      return {
        countryGroups: countryGroups.map((countryGroup, index) => ({ countryGroup, query: results[index] })),
        targets: discovered.flatMap((data) => data.targets),
        discoveryErrors: discovered.flatMap((data) => data.discoveryErrors),
        isPending: results.some((result) => result.isPending),
      };
    },
    [countryGroups],
  );

  return useQueries({
    queries: countryGroups.map((countryGroup) => ({
      queryKey: heroPlannerScheduleDebugQueryKeys.targets(brand, countryGroup.id),
      queryFn: () => discoverHeroPlannerDebugTargets(brand, countryGroup),
      enabled: !!brand,
      staleTime: Infinity,
    })),
    combine,
  });
}

/**
 * Loads a country group's hero planner rows for the send date, or undefined when they can't all be loaded - a partial
 * list could match segments to the wrong row.
 */
async function fetchCountryGroupHeroRows(
  brand: string,
  sendDate: string,
  countryGroupId: string,
  targets: Array<HeroPlannerDebugTarget>,
  signal?: AbortSignal,
): Promise<Array<CustomerCommunication.HeroPlannerSchedulesListItem> | undefined> {
  const rows = await runLimited(
    () => requestGetHeroPlannerSchedulesList(brand, sendDate, sendDate, countryGroupId),
    signal,
  );
  if (rows.length < HERO_PLANNER_LIST_MAX_ROWS) {
    return rows;
  }

  // the country group list was cut off, so load it a state at a time - each state's list stays well under the cap
  const rowsByLocation = await Promise.all(
    getHeroPlannerDebugRowLocations(targets, countryGroupId).map(({ country, state }) =>
      runLimited(
        () =>
          requestGetHeroPlannerStateSchedulesList(brand, sendDate, sendDate, countryGroupId, country, state, signal),
        signal,
      ),
    ),
  );
  if (rowsByLocation.some((locationRows) => locationRows.length >= HERO_PLANNER_LIST_MAX_ROWS)) {
    return undefined;
  }

  return uniqBy(rowsByLocation.flat(), 'id');
}

async function fetchHeroPlannerDebugHeroRows(
  brand: string,
  sendDate: string,
  countryGroupId: string,
  targets: Array<HeroPlannerDebugTarget>,
  signal?: AbortSignal,
): Promise<HeroPlannerDebugHeroRowsResult> {
  const heroRows: HeroPlannerDebugHeroRows = new Map();
  let rows: Array<CustomerCommunication.HeroPlannerSchedulesListItem> | undefined;
  try {
    rows = await fetchCountryGroupHeroRows(brand, sendDate, countryGroupId, targets, signal);
  } catch (error) {
    // let react-query discard cancelled lookups; any other failure just means no fallback rows
    if (signal?.aborted) {
      throw error;
    }
  }

  if (rows) {
    heroRows.set(countryGroupId, rows);
  }
  return { heroRows, isIncomplete: !rows };
}

function combineHeroPlannerDebugHeroRows(
  countryGroupIds: Array<string>,
  results: Array<UseQueryResult<HeroPlannerDebugHeroRowsResult>>,
): HeroPlannerDebugCountryGroupHeroRows {
  return {
    heroRows: new Map(results.flatMap((result) => (result.data ? [...result.data.heroRows] : []))),
    // disabled queries also report pending, so only treat a group as loading while it's actually fetching
    pendingCountryGroupIds: new Set(
      countryGroupIds.filter((_id, index) => results[index].isPending && results[index].fetchStatus !== 'idle'),
    ),
    incompleteCountryGroupIds: countryGroupIds.filter(
      (_id, index) => results[index].isError || !!results[index].data?.isIncomplete,
    ),
  };
}

/**
 * Every hero planner row on the send date for each country group - the same list the Hero Planner page shows. Used to
 * explain segments the schedules endpoint can't answer yet, and to know which cities get the experience email.
 */
export function useGetHeroPlannerDebugHeroRows(
  brand: string,
  sendDate: string,
  countryGroupTargets: Array<HeroPlannerDebugCountryGroupTargets>,
): HeroPlannerDebugCountryGroupHeroRows {
  const countryGroupIds = countryGroupTargets.map(({ countryGroup }) => countryGroup.id);
  const countryGroupIdsKey = countryGroupIds.join(',');
  const combine = useCallback(
    (results: Array<UseQueryResult<HeroPlannerDebugHeroRowsResult>>) =>
      combineHeroPlannerDebugHeroRows(countryGroupIdsKey ? countryGroupIdsKey.split(',') : [], results),
    [countryGroupIdsKey],
  );

  return useQueries({
    queries: countryGroupTargets.map(({ countryGroup, query }) => ({
      queryKey: heroPlannerScheduleDebugQueryKeys.heroRows(brand, sendDate, countryGroup.id),
      queryFn: ({ signal }) =>
        fetchHeroPlannerDebugHeroRows(brand, sendDate, countryGroup.id, query.data?.targets ?? [], signal),
      // waits for the country group's targets, which the state-by-state fallback needs
      enabled: !!brand && !!sendDate && !!query.data,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    })),
    combine,
  });
}

/**
 * A segment being re-checked reads as still checking rather than showing its previous result. react-query structurally
 * shares this combined list, so it keeps its identity until a result actually changes.
 */
function combineHeroPlannerDebugOutcomes(
  results: Array<UseQueryResult<HeroPlannerScheduleDebugOutcome>>,
): Array<HeroPlannerScheduleDebugOutcome | undefined> {
  return results.map((result) => (result.isFetching ? undefined : result.data));
}

/**
 * Resolves the schedule each target would receive on `sendDate`, in the same order as `targets` (undefined while
 * checking). One query per target so results stream into the UI as they land and a single segment can be re-checked
 * on its own.
 */
export function useGetHeroPlannerDebugSchedules(
  brand: string,
  sendDate: string,
  targets: Array<HeroPlannerDebugTarget>,
) {
  return useQueries({
    queries: targets.map((target) => ({
      queryKey: heroPlannerScheduleDebugQueryKeys.schedule(brand, sendDate, target.key),
      queryFn: ({ signal }): Promise<HeroPlannerScheduleDebugOutcome> =>
        runLimited(() => requestGetTodaysEscapesScheduleDebug(brand, sendDate, target, signal), signal),
      enabled: !!brand && !!sendDate,
      // HTTP failures are returned as data, so a retry would only re-run the same failing lookup
      retry: false,
      // a full sweep is hundreds of requests, so only run it when asked to
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    })),
    combine: combineHeroPlannerDebugOutcomes,
  });
}

/** Re-checks a single segment, e.g. after fixing its hero planner row */
export function useRecheckHeroPlannerDebugSchedule(brand: string, sendDate: string) {
  const queryClient = useQueryClient();

  return useCallback(
    (targetKey: string) =>
      queryClient.refetchQueries({
        queryKey: heroPlannerScheduleDebugQueryKeys.schedule(brand, sendDate, targetKey),
        exact: true,
      }),
    [brand, queryClient, sendDate],
  );
}

/**
 * The backend's own pre-send check for each country group (per segment for lifestyle brands). It covers what the
 * per-segment sweep can't see - every cadence and loyalty variant, hero planner rows edited after tail deals were
 * generated, custom schedule approval and the Braze destination catalog - so it's the source of truth for "will the
 * send go out".
 */
export function useGetHeroPlannerDebugPreSendChecks(
  brand: string,
  sendDate: string,
  countryGroupIds: Array<string>,
): Array<HeroPlannerDebugPreSendCheck> {
  const checks = countryGroupIds.flatMap((countryGroup) =>
    getHeroPlannerGenerationSegments(brand).map((segment) => ({ countryGroup, segment })),
  );

  const results = useQueries({
    queries: checks.map(({ countryGroup, segment }) => ({
      queryKey: heroPlannerScheduleDebugQueryKeys.preSendCheck(brand, sendDate, countryGroup, segment),
      queryFn: ({ signal }) =>
        requestGetTodaysEscapesScheduleGenerationStatus({ brand, sendDate, countryGroup, segment }, signal),
      enabled: !!brand && !!sendDate,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    })),
  });

  return checks.map((check, index) => ({ ...check, query: results[index] }));
}

/**
 * Clears the selected country groups' results for the send date and runs their sweep again, leaving other country
 * groups' cached results alone. Resetting (rather than refetching) puts rows back into their loading state so the
 * progress bar and counts reflect the new run rather than stale results.
 */
export function useRerunHeroPlannerDebugSchedules(brand: string, sendDate: string, countryGroupIds: Array<string>) {
  const queryClient = useQueryClient();

  return useCallback(() => {
    const targetKeyIndex = heroPlannerScheduleDebugQueryKeys.schedule(brand, sendDate, '').length - 1;

    return Promise.all(
      countryGroupIds.flatMap((countryGroupId) => [
        // hero rows too, so never-sent segments and fallbacks reflect the latest hero planner edits
        queryClient.resetQueries({
          queryKey: heroPlannerScheduleDebugQueryKeys.heroRows(brand, sendDate, countryGroupId),
          exact: true,
        }),
        // schedule keys end with the target key, which starts with the country group
        queryClient.resetQueries({
          queryKey: heroPlannerScheduleDebugQueryKeys.schedules(),
          predicate: (query) =>
            query.queryKey[targetKeyIndex - 2] === brand &&
            query.queryKey[targetKeyIndex - 1] === sendDate &&
            typeof query.queryKey[targetKeyIndex] === 'string' &&
            query.queryKey[targetKeyIndex].startsWith(`${countryGroupId}|`),
        }),
        queryClient.resetQueries({
          queryKey: [...heroPlannerScheduleDebugQueryKeys.preSendChecks(), brand, sendDate, countryGroupId],
        }),
      ]),
    );
  }, [brand, countryGroupIds, queryClient, sendDate]);
}
